import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";

// Shape-to-object mapping
const shapeObjectMap = [
    "circle.png", "square.png", "triangle.png", "star.png", "diamond.png", "rectangle.png", "pentagon.png"
];

interface ImagePair {
    left_id: number;
    right_id: number;
    leftObjects: string;
    rightObjects: string;
}

// Function to shuffle an array
const shuffleArray = (array: any[]) => array.sort(() => Math.random() - 0.5);

const MatchShapes: React.FC = () => {
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedShape, setSelectedShape] = useState<{ id: number; x: number; y: number } | null>(null);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [height, setHeight] = useState('0px');
    const modal = useRef<HTMLIonModalElement>(null);

    // Function to generate dynamic questions
    const generateQuestions = () => {
        const shuffledShapes = shuffleArray(shapeObjectMap).slice(0, 4); // Pick 4 random shapes
        const newObjects = shuffledShapes.map((shape, index) => ({
            id: index + 1,
            shape
        }));

        //console.log(newObjects)

        const suffledobject = shuffleArray([...newObjects]);
        const newQuestions: ImagePair[] = [];
        newObjects.map((object, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: newObjects.indexOf(suffledobject[index]) + 1,
                leftObjects: object.shape,
                rightObjects: suffledobject[index].shape,
            });
        })

        console.log(newQuestions);

        setQuestions(newQuestions);
        setConnections([]);
        setSelectedShape(null);
    };

    useEffect(() => {
        generateQuestions();
        document.title = "Match the Shapes – Shape Recognition for UKG | Mama's Guidance"; // Set page title
    }, []);

    // Get position relative to container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect();
        setHeight(container.scrollHeight + 'px');

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle shape click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedShape({ id, x: position.x, y: position.y });
    };

    // Handle object click
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedShape) {
            const position = getRelativePosition(event.currentTarget);
            const isCorrect = selectedShape.id === rightId;

            setConnections([...connections, { x1: selectedShape.x, y1: selectedShape.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedShape(null);
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Engage your UKG child with fun 'Match the Shapes' activities. Help children identify and connect 2D shapes with real-world objects. Free printable worksheets available." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/shapes/match-shapes" />
            </Helmet>
            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
            <IonModal
                ref={modal}
                trigger="open-modal"
                initialBreakpoint={1}
                breakpoints={[0, 0.5, 0.75, 1]}
                handleBehavior="cycle"
                backdropDismiss={false}  // ✅ Prevents click-to-close
                className="full-page-modal"
            >
                <IonContent className="ion-padding">
                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                    <h1>Match the Shapes</h1>

                    <p>
                        Shape learning is an exciting part of early math education. In this interactive <strong>“Match the Shapes”</strong> activity, UKG students will match basic 2D shapes to everyday objects, helping them strengthen visual recognition, observation, and classification skills.
                    </p>

                    <h2>🔷 Why Shape Matching Is Important</h2>
                    <ul>
                        <li>Enhances visual memory and shape identification</li>
                        <li>Builds connection between abstract shapes and real objects</li>
                        <li>Supports cognitive and logical development</li>
                        <li>Forms the base for geometry and spatial learning</li>
                    </ul>

                    <h2>🔺 Common Shapes Covered</h2>
                    <ul>
                        <li>Circle</li>
                        <li>Square</li>
                        <li>Triangle</li>
                        <li>Rectangle</li>
                        <li>Oval</li>
                        <li>Star</li>
                        <li>Heart</li>
                    </ul>

                    <h2>🧠 What Will Your Child Learn?</h2>
                    <ul>
                        <li>Identify basic shapes accurately</li>
                        <li>Match shapes with real-world objects (e.g., clock → circle)</li>
                        <li>Understand differences in shape sizes and edges</li>
                        <li>Improve problem-solving and logical thinking</li>
                    </ul>


                    <h2>🔗 Related Topics</h2>
                    <div>
                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Smallest Number</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Biggest Number</IonLabel>
                        </IonItem>
                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                            routerDirection="none"
                            lines="none"
                            detail={true}>
                            <IonLabel>Number Comparison</IonLabel>
                        </IonItem>
                    </div>

                    <h2>🏠 Practice Ideas at Home</h2>
                    <ul>
                        <li>Use blocks or toys to match shapes by touch and sight</li>
                        <li>Point out real-world shapes at home or during walks</li>
                        <li>Cut out paper shapes and match them to household items</li>
                        <li>Use shape-sticker activities to reinforce recognition</li>
                    </ul>

                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                </IonContent>
            </IonModal>
            <article className="visually-hidden">
                <h1>Match the Shapes</h1>

                <p>
                    Shape learning is an exciting part of early math education. In this interactive <strong>“Match the Shapes”</strong> activity, UKG students will match basic 2D shapes to everyday objects, helping them strengthen visual recognition, observation, and classification skills.
                </p>

                <h2>🔷 Why Shape Matching Is Important</h2>
                <ul>
                    <li>Enhances visual memory and shape identification</li>
                    <li>Builds connection between abstract shapes and real objects</li>
                    <li>Supports cognitive and logical development</li>
                    <li>Forms the base for geometry and spatial learning</li>
                </ul>

                <h2>🔺 Common Shapes Covered</h2>
                <ul>
                    <li>Circle</li>
                    <li>Square</li>
                    <li>Triangle</li>
                    <li>Rectangle</li>
                    <li>Oval</li>
                    <li>Star</li>
                    <li>Heart</li>
                </ul>

                <h2>🧠 What Will Your Child Learn?</h2>
                <ul>
                    <li>Identify basic shapes accurately</li>
                    <li>Match shapes with real-world objects (e.g., clock → circle)</li>
                    <li>Understand differences in shape sizes and edges</li>
                    <li>Improve problem-solving and logical thinking</li>
                </ul>


                <h2>🔗 Related Topics</h2>
                <div>
                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Smallest Number</IonLabel>
                    </IonItem>
                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Biggest Number</IonLabel>
                    </IonItem>
                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                        routerDirection="none"
                        lines="none"
                        detail={true}>
                        <IonLabel>Number Comparison</IonLabel>
                    </IonItem>
                </div>

                <h2>🏠 Practice Ideas at Home</h2>
                <ul>
                    <li>Use blocks or toys to match shapes by touch and sight</li>
                    <li>Point out real-world shapes at home or during walks</li>
                    <li>Cut out paper shapes and match them to household items</li>
                    <li>Use shape-sticker activities to reinforce recognition</li>
                </ul>
            </article>
            <div ref={containerRef} className="contentPart additionWord matchObject match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                {/* SVG for connections */}
                <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                    {connections.map((conn, index) => (
                        <line key={index} x1={conn.x1} y1={conn.y1} x2={conn.x2} y2={conn.y2} stroke={conn.correct ? "green" : "red"} strokeWidth="3" />
                    ))}
                </svg>


                <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%", height: "100%" }}>
                    {/* Left Column - Shapes */}
                    <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                        <div className="left-column">
                            {questions.map((item) => (
                                <div key={item.left_id} className={`image-box ${selectedShape?.id === item.left_id ? "selected" : ""}`} onClick={(e) => handleLeftClick(e, item.left_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                    <img src={process.env.REACT_APP_API_URL + item.leftObjects} />
                                </div>
                            ))}
                        </div>
                        <div className="right-column">
                            {questions.map((item) => (
                                <div key={item.right_id} className="image-box" onClick={(e) => handleRightClick(e, item.right_id)} style={{ cursor: "pointer", padding: "10px" }}>
                                    <img src={process.env.REACT_APP_API_URL + item.rightObjects} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Controls */}
                <div className="text-center">
                    <button className="btn hero-btn" onClick={generateQuestions}>Next Question</button>
                </div>
            </div>
        </>
    );
};

export default MatchShapes;
